import type { RemunerationTemplateThirteenthMonthUnit } from "../types/supabase.types.gen";

/**
 * Computes the amount subject to contributions (taxable): max(0, amount - non-taxable ceiling).
 */
export const computeAmountTaxable = (amount: number, amountNonTaxable: number): number =>
  Math.max(0, amount - amountNonTaxable);

const remunerationDecimalFormatter = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** French display of a remuneration number: always 2 fraction digits (`10,40`, `1,00`). */
export const formatRemunerationDecimal = (value: number): string => remunerationDecimalFormatter.format(value);

/** Coefficient read-only label. Missing values display as `1,00`. */
export const formatRemunerationCoefficient = (coefficient: number | undefined): string =>
  formatRemunerationDecimal(coefficient ?? 1);

export const roundRemunerationAmountToCents = (value: number): number => Math.round(value * 100) / 100;

export const normalizeThirteenthMonthUnit = (
  thirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit | null | undefined
): RemunerationTemplateThirteenthMonthUnit => thirteenthMonthUnit ?? "hour";

/** Trailing currency symbol for 13th month amounts: `€/h` or `€/j`. */
export const formatThirteenthMonthCurrencySymbol = (
  thirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit | null | undefined
): string => (normalizeThirteenthMonthUnit(thirteenthMonthUnit) === "day" ? "€/j" : "€/h");

/**
 * 13th month bonus (hourly): hourly rate / 12
 */
export const computeThirteenthMonthPrimeHourly = (hourlyRate: number): number => hourlyRate / 12;

/**
 * 13th month bonus (daily): (hourly rate × 7) / 12
 */
export const computeThirteenthMonthPrimeDaily = (hourlyRate: number): number => (hourlyRate * 7) / 12;

export const computeThirteenthMonthPrime = (
  hourlyRate: number,
  thirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit
): number =>
  roundRemunerationAmountToCents(
    thirteenthMonthUnit === "day"
      ? computeThirteenthMonthPrimeDaily(hourlyRate)
      : computeThirteenthMonthPrimeHourly(hourlyRate)
  );

export const resolveThirteenthMonthAmount = (options: {
  hourlyRate: number;
  thirteenthMonthEnabled: boolean;
  thirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit | null | undefined;
  thirteenthMonthAmount: number | null | undefined;
}): number | undefined => {
  if (!options.thirteenthMonthEnabled) {
    return undefined;
  }
  if (typeof options.thirteenthMonthAmount === "number") {
    return options.thirteenthMonthAmount;
  }
  return computeThirteenthMonthPrime(options.hourlyRate, normalizeThirteenthMonthUnit(options.thirteenthMonthUnit));
};

export const isThirteenthMonthAmountEqualToTheoretical = (
  hourlyRate: number,
  thirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit,
  thirteenthMonthAmount: number | null | undefined
): boolean => {
  if (typeof thirteenthMonthAmount !== "number") {
    return true;
  }
  return (
    roundRemunerationAmountToCents(thirteenthMonthAmount) ===
    computeThirteenthMonthPrime(hourlyRate, thirteenthMonthUnit)
  );
};

type ThirteenthMonthProfilePatch = Partial<{
  hourly_rate: number;
  thirteenth_month_enabled: boolean;
  thirteenth_month_unit: RemunerationTemplateThirteenthMonthUnit;
  thirteenth_month_amount: number | undefined;
}>;

/** Recalculates the 13th month amount only while it still matches the theoretical formula. */
export const nextThirteenthMonthAmountAfterProfileChange = (options: {
  previousHourlyRate: number;
  previousThirteenthMonthUnit: RemunerationTemplateThirteenthMonthUnit;
  previousThirteenthMonthEnabled: boolean;
  previousThirteenthMonthAmount: number | undefined;
  patch: ThirteenthMonthProfilePatch;
}): number | undefined => {
  const nextThirteenthMonthEnabled = options.patch.thirteenth_month_enabled ?? options.previousThirteenthMonthEnabled;
  if (!nextThirteenthMonthEnabled) {
    return undefined;
  }

  if ("thirteenth_month_amount" in options.patch) {
    return options.patch.thirteenth_month_amount;
  }

  const nextHourlyRate = options.patch.hourly_rate ?? options.previousHourlyRate;
  const nextThirteenthMonthUnit = options.patch.thirteenth_month_unit ?? options.previousThirteenthMonthUnit;

  if (options.patch.thirteenth_month_enabled === true && !options.previousThirteenthMonthEnabled) {
    return computeThirteenthMonthPrime(nextHourlyRate, nextThirteenthMonthUnit);
  }

  const hourlyRateChanged = options.patch.hourly_rate !== undefined;
  const thirteenthMonthUnitChanged = options.patch.thirteenth_month_unit !== undefined;
  if (!hourlyRateChanged && !thirteenthMonthUnitChanged) {
    return options.previousThirteenthMonthAmount;
  }

  if (
    isThirteenthMonthAmountEqualToTheoretical(
      options.previousHourlyRate,
      options.previousThirteenthMonthUnit,
      options.previousThirteenthMonthAmount
    )
  ) {
    return computeThirteenthMonthPrime(nextHourlyRate, nextThirteenthMonthUnit);
  }

  return options.previousThirteenthMonthAmount;
};

export const exceedsNonTaxablePlafond = (
  amountNonTaxable: number,
  maxAmountNonTaxable: number | undefined
): boolean => {
  if (typeof maxAmountNonTaxable !== "number") {
    return false;
  }
  return amountNonTaxable > maxAmountNonTaxable;
};
